import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Memory as ComputeIcon,
  Person as ExpertIcon,
  SmartToy as AgentIcon,
  Storage as SystemIcon,
} from '@mui/icons-material';
import { billingApi, UsageBreakdown } from '../../api/billing';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  formatModelPricing,
  formatModelSummary,
  formatModelSummaryTooltip,
} from '../../utils/modelPricing';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color = 'primary.main' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ color, mr: 1.5 }}>{icon}</Box>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const ModelCell: React.FC<{ models?: any[] }> = ({ models }) => {
  const first = models?.[0];
  return (
    <Tooltip
      title={<Box sx={{ whiteSpace: 'pre-line' }}>{formatModelSummaryTooltip(models)}</Box>}
      placement="top"
      arrow
    >
      <Box sx={{ minWidth: 160 }}>
        <Typography variant="body2" noWrap>
          {formatModelSummary(models)}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {first ? formatModelPricing(first) : 'Input/output price not recorded'}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const CostDashboard: React.FC = () => {
  const [days, setDays] = useState(30);
  const [breakdownTab, setBreakdownTab] = useState(0);

  const { data, isLoading, error } = useQuery<UsageBreakdown>({
    queryKey: ['cost-insights', days],
    queryFn: () => billingApi.getUsage(days),
  });

  const summary = useMemo(() => {
    if (!data) {
      return null;
    }

    const systemSpend = data.systems.reduce((total, system) => total + system.total_cost, 0);
    const systemRuns = data.systems.reduce((total, system) => total + system.execution_count, 0);
    const agentSpend = data.agents.reduce((total, agent) => total + agent.total_cost, 0);
    const agentRuns = data.agents.reduce((total, agent) => total + agent.execution_count, 0);
    const expertRuns = data.experts.reduce((total, expert) => total + expert.execution_count, 0);
    const topSystem = data.systems[0] ?? null;

    return {
      systemSpend,
      systemRuns,
      agentSpend,
      agentRuns,
      expertRuns,
      topSystem,
    };
  }, [data]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load cost insights: {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  const hasAnyCostData = data.systems.length > 0 || data.experts.length > 0 || data.agents.length > 0;

  const systemChartData = data.systems.map((system) => ({
    name: system.system_name.length > 20 ? `${system.system_name.slice(0, 20)}…` : system.system_name,
    experts: system.experts_cost,
    compute: system.compute_cost,
    total: system.total_cost,
    executions: system.execution_count,
  }));

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Cost
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track where spend is accumulating across systems, experts, and agents in one period view.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Period</InputLabel>
          <Select value={days} label="Period" onChange={(event) => setDays(Number(event.target.value))}>
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={14}>Last 14 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {!hasAnyCostData && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No cost data is available yet. Run a system or agent to start building cost insights.
        </Alert>
      )}

      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="System Spend"
              value={`$${summary.systemSpend.toFixed(4)}`}
              subtitle={`${summary.systemRuns.toLocaleString()} system runs`}
              icon={<SystemIcon fontSize="large" />}
              color="primary.main"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Agent Spend"
              value={`$${summary.agentSpend.toFixed(4)}`}
              subtitle={`${summary.agentRuns.toLocaleString()} agent runs`}
              icon={<AgentIcon fontSize="large" />}
              color="secondary.main"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Expert Executions"
              value={summary.expertRuns.toLocaleString()}
              subtitle={`${data.experts.length} experts with spend`}
              icon={<ExpertIcon fontSize="large" />}
              color="info.main"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Top System"
              value={summary.topSystem ? `$${summary.topSystem.total_cost.toFixed(4)}` : '$0.0000'}
              subtitle={summary.topSystem?.system_name || `No system spend in the last ${days} days`}
              icon={<MoneyIcon fontSize="large" />}
              color="success.main"
            />
          </Grid>
        </Grid>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Cost by System
          </Typography>
          {systemChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={systemChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(value: number) => `$${value.toFixed(2)}`} />
                <RechartsTooltip formatter={(value: number) => `$${value.toFixed(4)}`} />
                <Legend />
                <Bar dataKey="experts" name="Experts" fill="#2196f3" stackId="cost" />
                <Bar dataKey="compute" name="Compute" fill="#ff9800" stackId="cost" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box
              sx={{
                height: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'action.hover',
                px: 3,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No system cost data for the selected period yet. This chart will populate after your first AI system runs.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                Breakdowns
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Compare detailed cost rows by system, expert, or agent without stacking multiple tables on the page.
              </Typography>
            </Box>
          </Box>

          <Tabs
            value={breakdownTab}
            onChange={(_, nextTab) => setBreakdownTab(nextTab)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mt: 2 }}
          >
            <Tab label="Systems" />
            <Tab label="Experts" />
            <Tab label="Agents" />
          </Tabs>

          <TabPanel value={breakdownTab} index={0}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>System</TableCell>
                    <TableCell>Models</TableCell>
                    <TableCell align="right">Experts</TableCell>
                    <TableCell align="right">Compute</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Runs</TableCell>
                    <TableCell align="right">Avg / Run</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.systems.length > 0 ? (
                    data.systems.map((system) => (
                      <TableRow key={system.system_id} hover>
                        <TableCell>{system.system_name}</TableCell>
                        <TableCell><ModelCell models={system.models} /></TableCell>
                        <TableCell align="right">${system.experts_cost.toFixed(4)}</TableCell>
                        <TableCell align="right">${system.compute_cost.toFixed(4)}</TableCell>
                        <TableCell align="right">${system.total_cost.toFixed(4)}</TableCell>
                        <TableCell align="right">{system.execution_count}</TableCell>
                        <TableCell align="right">
                          ${system.execution_count > 0 ? (system.total_cost / system.execution_count).toFixed(4) : '0.0000'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No AI system spend has been recorded in this period yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={breakdownTab} index={1}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Expert</TableCell>
                    <TableCell>Model Snapshot</TableCell>
                    <TableCell align="right">Total Cost</TableCell>
                    <TableCell align="right">Executions</TableCell>
                    <TableCell align="right">Avg Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.experts.length > 0 ? (
                    data.experts.map((expert) => (
                      <TableRow key={expert.expert_id} hover>
                        <TableCell>{expert.expert_name}</TableCell>
                        <TableCell><ModelCell models={expert.models} /></TableCell>
                        <TableCell align="right">${expert.total_cost.toFixed(4)}</TableCell>
                        <TableCell align="right">{expert.execution_count}</TableCell>
                        <TableCell align="right">
                          ${expert.execution_count > 0 ? (expert.total_cost / expert.execution_count).toFixed(4) : '0.0000'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No expert cost data has been recorded in this period yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={breakdownTab} index={2}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Agent</TableCell>
                    <TableCell>Model Snapshot</TableCell>
                    <TableCell align="right">Total Cost</TableCell>
                    <TableCell align="right">Executions</TableCell>
                    <TableCell align="right">Avg Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.agents.length > 0 ? (
                    data.agents.map((agent) => (
                      <TableRow key={agent.agent_id} hover>
                        <TableCell>{agent.agent_name}</TableCell>
                        <TableCell><ModelCell models={agent.models} /></TableCell>
                        <TableCell align="right">${agent.total_cost.toFixed(4)}</TableCell>
                        <TableCell align="right">{agent.execution_count}</TableCell>
                        <TableCell align="right">
                          ${agent.execution_count > 0 ? (agent.total_cost / agent.execution_count).toFixed(4) : '0.0000'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No agent cost data has been recorded in this period yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CostDashboard;
